/**
 * Error-text sanitizer for persisted / exported error strings (#186 log hygiene).
 *
 * Error messages come from many producers — the eligibility vendor's API,
 * the portal scrapers, transform validation, config checks — and free text from those
 * can echo request identifiers (member IDs, MBIs, dates of birth). The strings
 * land on check/run docs whose `lastError` surfaces on cross-org staff
 * dashboards, and they ride into Sentry exception messages (no BAA), so
 * identifiers are masked once at each sink rather than audited
 * producer-by-producer forever.
 *
 * Pattern-based: anything identifier-shaped becomes a placeholder while the
 * prose stays readable ("Payer rejected member id [id], dob [date]"). Short
 * numbers survive on purpose — HTTP statuses ("returned 500") and AAA codes
 * ("AAA 42") are the error taxonomy staff triage by.
 *
 * NAMED LIMITS — what this deliberately does NOT catch (masking these
 * reliably in free text is not possible without destroying the message):
 *  - person names in prose ("no member found for Jane Doe" keeps the name);
 *  - dates written out in words ("June 15, 1985");
 *  - short mixed ids with fewer than 4 digits ("AB12CD").
 * The mitigation for names is upstream: our own messages never interpolate
 * patient fields, and bot/vendor messages that might are additionally length-
 * bounded and (for UI) vendor-scrubbed at render.
 */

const MAX_ERROR_LEN = 500;

/**
 * Mask identifier-shaped content in an error message. Never throws.
 *
 * `maxLen` defaults to the 500 every existing caller relies on; the scraper
 * passes a larger bound when it prints a stack, where truncating at 500 would
 * cut the frames that make the trace useful.
 */
export function sanitizeErrorText(text: string, maxLen = MAX_ERROR_LEN): string {
  let out = text;
  // Emails (a subscriber contact echoed into a validation error).
  out = out.replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]");
  // Dates — ISO (1985-06-15) and US (6/15/1985 or 06/15/85): birth dates are
  // the identifier most often echoed by payer rejections.
  out = out.replace(/\b\d{4}-\d{2}-\d{2}\b/g, "[date]");
  out = out.replace(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g, "[date]");
  // Id-shaped tokens: 9+ chars of letters/digits/dashes carrying 4+ digits —
  // member ids ("W123456789"), MBIs ("1EG4-TE5-MK73"), policy numbers. Runs
  // after the date passes so a date is labeled [date], not [id].
  out = out.replace(/\b[A-Za-z0-9-]{9,}\b/g, (tok) =>
    (tok.match(/\d/g)?.length ?? 0) >= 4 ? "[id]" : tok);
  // Bare digit runs (SSNs, member ids without letters). 5+ keeps HTTP
  // statuses and reject codes readable.
  out = out.replace(/\d{5,}/g, "[id]");
  // Bounded: errorHistory arrays accumulate — a runaway vendor response body
  // must not grow the doc unbounded.
  if (out.length > maxLen) out = out.slice(0, maxLen) + "…";
  return out;
}
